<template>
  <div ref="ppm" class="ppm-container">
    <div v-if="listName && !hideName" class="title">
      <h2>{{ listName }}</h2>
    </div>
    <div v-if="linkToMore" class="link">
      <nuxt-link
        :to="linkToMore"
        class="nav-link text-primary underline decoration-solid text-center"
      >
        Alle Artikel anzeigen
      </nuxt-link>
    </div>
    <template v-if="4 >= slides.length">
      <div class="hidden lg:grid lg:grid-cols-5 slider gap-4 py-4 lg:py-0">
        <div v-for="slide in slides" :key="slide.id">
          <basic-ppm-card-article
            v-if="slide.type === 'article'"
            class="ppm-slide"
            :slide="slide"
            :is-sponsored="isSponsored"
          />
        </div>
      </div>
      <BasicSlider
        class="slider lg:hidden"
        :slides="slides ?? []"
        :config="carouselConfig"
        :data-beacon-view-ping="viewBeacon"
      >
        <template #default="{ slide }">
          <basic-ppm-card-article
            v-if="slide.type === 'article'"
            class="ppm-slide"
            :slide="slide"
            :is-sponsored="isSponsored"
          />
        </template>
      </BasicSlider>
    </template>
    <template v-else>
      <BasicSlider
        class="slider"
        :slides="slides ?? []"
        :config="carouselConfig"
        :data-beacon-view-ping="viewBeacon"
      >
        <template #default="{ slide }">
          <basic-ppm-card-article
            v-if="slide.type === 'article'"
            class="ppm-slide"
            :slide="slide"
            :is-sponsored="isSponsored"
          />
        </template>
      </BasicSlider>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { TemplateRef } from "vue";
import { getImpressionsForDataLayer } from "~/composables/gtaUtils";
import { useCriteoBeacons, usePlacementBeacon } from "~/composables/sponsoring";
import { CarouselAlignment } from "~~/models/NuxtCarousel";

interface Props {
  slides: Array<ArticleSlide | WarrantySlide>;
  isSponsored?: boolean;
  listName?: string;
  hideName?: boolean;
  linkToMore?: string;
  viewBeacon?: string;
  loadBeacon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isSponsored: false,
  listName: "",
  hideName: false,
  linkToMore: "",
  viewBeacon: "",
  loadBeacon: "",
});

const { placementBeaconString } = usePlacementBeacon();

const ppm: TemplateRef<HTMLElement> = useTemplateRef<HTMLElement>("ppm");
const carouselConfig = ref({
  height: 460,
  gap: 15,
  breakpoints: {
    1000: {
      itemsToShow: 5.5,
      itemsToScroll: 5,
      snapAlign: CarouselAlignment.START,
    },
    900: {
      itemsToShow: 4,
      itemsToScroll: 4,
      snapAlign: CarouselAlignment.START,
    },
  },
});

if (props.isSponsored) {
  const loadBeacon = props.loadBeacon + placementBeaconString.value;

  useCriteoBeacons(ppm, {
    loadBeacon: loadBeacon,
    viewBeacon: props.viewBeacon,
  });
}

if (import.meta.client) {
  getImpressionsForDataLayer(props.slides, props?.listName);
}
</script>

<style scoped lang="postcss">
.ppm-container {
  display: grid;
  grid-template-areas:
    "title title"
    "slider slider"
    "link link";
  grid-template-columns: repeat(2, 1fr);
}

.title {
  @apply text-lg font-bold;
  grid-area: title;
}

.link {
  grid-area: link;
}

.slider {
  grid-area: slider;
}

.ppm-slide {
  height: 100%;
  width: 100%;
  margin: 0 auto;
}

@media (min-width: 1200px) {
  .ppm-container {
    grid-template-areas:
      "title link"
      "slider slider";
    row-gap: 0.5rem;
  }

  .link {
    display: flex;
    justify-content: end;
  }

  .title {
    display: flex;
    align-items: center;
  }
}
</style>
